import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
} from "react-router-dom";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectSession from "../dialog/MdlSelectSession";
import PaymentDetails from "./PaymentDetails";
import FeeReceipt from "./FeeReceipt";

const HEADER_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;
const ACCENT = COLORS.primary_color_shade5;
const ACCENT_DARK = COLORS.primary_color_shade7;
const ACCENT_SHADOW = "rgba(3, 169, 244, 0.28)";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const TYPE_THEME = {
  FEE: {
    accent: COLORS.card_green || "#89BE4C",
    soft: "rgba(137, 190, 76, 0.12)",
    border: "rgba(137, 190, 76, 0.45)",
    label: "Fee",
    icon: "fa-check",
  },
  OLD_DUE: {
    accent: COLORS.card_red || "#ef5350",
    soft: "rgba(239, 83, 80, 0.12)",
    border: "rgba(239, 83, 80, 0.45)",
    label: "Old Due",
    icon: "fa-exclamation",
  },
  DEFAULT: {
    accent: ACCENT,
    soft: "rgba(3, 169, 244, 0.12)",
    border: "rgba(3, 169, 244, 0.38)",
    label: "Other",
    icon: "fa-rupee",
  },
};

function getTypeTheme(transactionFor) {
  return TYPE_THEME[transactionFor] || {
    ...TYPE_THEME.DEFAULT,
    label: transactionFor || "Payment",
  };
}

function formatAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `₹${amount ?? "0"}`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function classLabel(student) {
  const cls = student?.class?.[0];
  if (!cls) return "—";
  const name = cls.class_name || "";
  const section = cls.section_name;
  return section ? `${name}:${section}` : name;
}

function normalizeSessionName(value) {
  if (value == null) return "";
  let name = String(value).trim();
  try {
    name = decodeURIComponent(name).trim();
  } catch (_) {
    /* keep raw */
  }
  if (!name || name === "undefined" || name === "null") return "";
  /* Reject session ids (e.g. SSN0004) passed in place of the name */
  if (/^SSN\d+$/i.test(name)) return "";
  return name;
}

function resolveSessionName(paramName, student) {
  const fromParam = normalizeSessionName(paramName);
  if (fromParam) return fromParam;

  const fromStudent = normalizeSessionName(student?.class?.[0]?.session_name);
  if (fromStudent) return fromStudent;

  const current = SessionManager.getCurrentSession?.();
  const fromManager = normalizeSessionName(
    Array.isArray(current) ? current[0]?.session_name : current?.session_name
  );
  return fromManager || "";
}

const StudentDetailsCard = memo(function StudentDetailsCard({ student, sessionName }) {
  const rollNo = student?.class?.[0]?.student_roll_no ?? "—";
  const cls = classLabel(student);

  const labelStyle = {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.35,
    textTransform: "uppercase",
    color: "#475569",
    lineHeight: 1.2,
    marginBottom: 1,
  };

  const valueStyle = {
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.25,
  };

    return ( 
    <div
      style={{
        marginBottom: 12,
        borderRadius: 12,
        border: `2px solid ${ACCENT_DARK}`,
        overflow: "hidden",
        background: COLORS.white,
        boxShadow: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "8px 12px",
          flexWrap: "wrap",
          background: `linear-gradient(90deg, ${COLORS.primary_color_shade0} 0%, ${COLORS.white} 55%)`,
        }}
      >
        <div
          style={{
            width: 78,
            height: 78,
            borderRadius: "50%",
            padding: 2,
            flexShrink: 0,
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade8} 100%)`,
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              overflow: "hidden",
              border: "2.5px solid #fff",
              background: `linear-gradient(180deg, ${COLORS.primary_color_shade0} 0%, #e2e8f0 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="fa fa-user"
              aria-hidden
              style={{
                fontSize: 32,
                color: COLORS.primary_color_shade3,
                position: "absolute",
              }}
            />
            <img
              src={student.student_img_path}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>  
      </div>

        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ marginBottom: 6 }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {student.stu_name}
                      </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: COLORS.white,
                  background: ACCENT_DARK,
                  borderRadius: 999,
                  padding: "2px 9px",
                }}
              >
                ID: {student.student_enrollment_no}
              </span>
              {sessionName ? (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#065f46",
                    background: "#d1fae5",
                    border: "1px solid #6ee7b7",
                    borderRadius: 999,
                    padding: "2px 9px",
                  }}
                  title={sessionName}
                >
                  {sessionName}
                </span>
              ) : null}
                        </div>
                  </div>
                 
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 6,
            }}
          >
            <div
              style={{
                padding: "5px 8px",
                borderRadius: 8,
                background: COLORS.white,
                border: "1.5px solid #cbd5e1",
                minWidth: 0,
              }}
            >
              <div style={labelStyle}>Father</div>
              <div style={valueStyle} title={student.father_name || "—"}>
                {student.father_name || "—"}
              </div>
            </div>
            
            <div
              style={{
                padding: "5px 8px",
                borderRadius: 8,
                background: COLORS.white,
                border: `1.5px solid ${COLORS.primary_color_shade3}`,
                minWidth: 0,
              }}
            >
              <div style={{ ...labelStyle, color: ACCENT_DARK }}>Class</div>
              <div style={{ ...valueStyle, color: ACCENT_DARK }} title={cls}>
                {cls}
            </div>
            </div>

            <div
              style={{
                padding: "5px 8px",
                borderRadius: 8,
                background: COLORS.white,
                border: "1.5px solid #6ee7b7",
                minWidth: 0,
              }}
            >
              <div style={{ ...labelStyle, color: "#047857" }}>Roll No</div>
              <div style={{ ...valueStyle, color: "#065f46" }}>{rollNo}</div>
            </div>
          </div>
     </div>
                          </div>
                     </div>
  );
});

const PaymentCard = memo(function PaymentCard({ item }) {
  const history = useHistory();
  const theme = getTypeTheme(item.transaction_for);
  const detailsTo = `/AdminMain/PaymentDetails/${item.transaction_id}/${item.session_id}/${item.student_id}/${item.transaction_for}`;
  const receiptTo = `/AdminMain/FeeReceipt/${item.transaction_id}/${item.student_id}/${item.session_id}`;

  const infoTile = {
    minWidth: 118,
    padding: "4px 10px",
    borderRadius: 10,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  };
  const infoLabel = {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: 2,
    display: "flex",
    alignItems: "center",
    gap: 4,
  };
  const infoValue = {
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    whiteSpace: "nowrap",
  };

  return (
    <div
      style={{
        height: "100%",
        borderRadius: 14,
        border: `1.5px solid ${theme.border}`,
        background: COLORS.white,
        overflow: "hidden",
        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 20px rgba(15, 23, 42, 0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 10px rgba(15, 23, 42, 0.06)";
      }}
    >
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div
          style={{
            width: 130,
            flexShrink: 0,
            background: `linear-gradient(180deg, ${theme.soft} 0%, #fff 100%)`,
            borderRight: `1px solid ${theme.border}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            padding: "8px 8px",
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: theme.accent,
              color: COLORS.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              boxShadow: "0 3px 8px rgba(15, 23, 42, 0.18)",
            }}
          >
            <i className={`fa ${theme.icon}`} aria-hidden />
          </div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: theme.accent,
              textAlign: "center",
              lineHeight: 1.15,
            }}
          >
            {formatAmount(item.paid_amount)}
          </div>
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: theme.accent,
              background: COLORS.white,
              border: `1px solid ${theme.border}`,
              borderRadius: 999,
              padding: "2px 9px",
            }}
          >
            {theme.label}
          </span>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            padding: "8px 14px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "stretch",
              gap: 10,
              width: "100%",
            }}
          >
            <div style={infoTile}>
              <div style={infoLabel}>
                <i className="fa fa-file-text-o" style={{ color: ACCENT_DARK }} aria-hidden />
                Receipt No
              </div>
              <div style={infoValue}>{item.reciept_no || "—"}</div>
            </div>
            <div style={infoTile}>
              <div style={infoLabel}>
                <i className="fa fa-hashtag" style={{ color: ACCENT_DARK }} aria-hidden />
                Transaction
              </div>
              <div style={infoValue}>{item.transaction_id || "—"}</div>
            </div>
            <div style={infoTile}>
              <div style={infoLabel}>
                <i className="fa fa-calendar" style={{ color: ACCENT_DARK }} aria-hidden />
                Txn Date
              </div>
              <div style={infoValue}>{item.transaction_date || "—"}</div>
            </div>
            <div style={{ ...infoTile, borderColor: "#bbf7d0", background: "#f0fdf4" }}>
              <div style={{ ...infoLabel, color: "#047857" }}>
                <i className="fa fa-calendar-check-o" aria-hidden />
                Collection
              </div>
              <div style={{ ...infoValue, color: "#065f46" }}>{item.collection_date || "—"}</div>
                        </div>
                      </div>
                  </div>
                 
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 6,
            padding: "8px 14px",
            borderLeft: `1px solid ${theme.border}`,
            background: "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={() => history.push(detailsTo)}
            style={{
              minWidth: 150,
              border: `1px solid ${ACCENT_DARK}`,
              borderRadius: 8,
              padding: "5px 12px",
              background: COLORS.white,
              color: ACCENT_DARK,
              fontSize: 11,
              fontWeight: 800,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
            }}
          >
            <i className="fa fa-info-circle" aria-hidden />
            Details
          </button>
          <button
            type="button"
            onClick={() => history.push(receiptTo)}
            style={{
              minWidth: 150,
              border: "none",
              borderRadius: 8,
              padding: "5px 12px",
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`,
              color: COLORS.white,
              fontSize: 11,
              fontWeight: 800,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
            }}
          >
            <i className="fa fa-file-text-o" aria-hidden />
            Get Fee Receipt
          </button>
        </div>
      </div>
                 </div>
  );
});

const PaymentHistory = () => {
  const {
    student_id: studentIdParam,
    session_id: sessionIdParam,
    session_name: sessionNameParam,
    session_status: sessionStatusParam,
  } = useParams();

  const loggedIn = localStorage.getItem("token") !== null;

  const [loading, setLoading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [studentDetails, setStudentDetails] = useState([]);
  const [sessionId, setSessionId] = useState(sessionIdParam || "");
  const [sessionName, setSessionName] = useState(
    () => resolveSessionName(sessionNameParam, null)
  );
  const [sessionStatus, setSessionStatus] = useState(
    normalizeSessionName(sessionStatusParam) || ""
  );
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);

  const student = studentDetails[0] || null;

  const resolvedSessionName = useMemo(
    () => resolveSessionName(sessionName, student),
    [sessionName, student]
  );
  const displaySessionName = resolvedSessionName || "Select Session";

  const getPaymentHistory = useCallback(
    (sid) => {
      const activeSessionId = sid || sessionId;
      if (!activeSessionId || !studentIdParam) return;

      setLoading(true);
      axios
        .get(`${API_BASE()}getPaymentHistory.php`, {
          params: {
            school_code: SessionManager.getSchoolCode(),
            session_id: activeSessionId,
            student_id: studentIdParam,
          },
        })
        .then((res) => res.data)
        .then((response) => {
          setLoading(false);
          if (response.api_status === "OK") {
            const details = response.data?.[0]?.student_details || [];
            setPaymentHistory(response.data?.[0]?.payment_history || []);
            setStudentDetails(details);
            setSessionName((prev) =>
              resolveSessionName(prev, details[0] || null) || prev
            );
          } else {
            setPaymentHistory([]);
            setStudentDetails([]);
            myToast.notify_warning(response.api_remark);
          }
        })
        .catch((error) => {
          setLoading(false);
          console.log("getPaymentHistory error:", error);
        });
    },
    [sessionId, studentIdParam]
  );

  useEffect(() => {
    if (sessionIdParam) {
      setSessionId(sessionIdParam);
      setSessionName(resolveSessionName(sessionNameParam, null));
      setSessionStatus(normalizeSessionName(sessionStatusParam) || "");
      getPaymentHistory(sessionIdParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdParam, sessionNameParam, sessionStatusParam, studentIdParam]);

  const handleCloseSessionDialog = useCallback(() => {
    setIsOpenSessionDialog(false);
  }, []);

  const handleSelectedSession = useCallback(
    (nextSessionId, nextSessionName, nextSessionStatus) => {
      setSessionId(nextSessionId);
      setSessionName(normalizeSessionName(nextSessionName) || resolveSessionName(null, null));
      setSessionStatus(normalizeSessionName(nextSessionStatus) || "");
      setIsOpenSessionDialog(false);
      getPaymentHistory(nextSessionId);
    },
    [getPaymentHistory]
  );

  const totalPaid = useMemo(() => {
    return (paymentHistory || []).reduce((sum, row) => {
      const n = Number(row.paid_amount);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [paymentHistory]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (loading && !student) {
    return <MyLoader />;
  }

  return (
    <Router>
      <Switch>
        <Route
          path="/AdminMain/PaymentHistory/:student_id/:session_id/:session_name"
          exact
          render={() => (
    <div className="content" style={{ background: COLORS.white }}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="#" style={{ color: COLORS.white }}>
                Payment History
              </a>
            </li>
          </ul>
                        </div>
        <div className="rightside-content-header">
          <BackButton />
                      </div>
                  </div>
                 
      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{
          "--mMargin": "0",
          padding: 0,
          overflow: "hidden",
          borderRadius: 14,
        }}
      >
        <div
          style={{
            background: HEADER_GRADIENT,
            color: "#fff",
            padding: "12px 40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            minHeight: 56,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "999px",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa fa-history" style={{ fontSize: 18 }} aria-hidden />
            </div>
            <div>
              <h5 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                Payment History
              </h5>
              <small style={{ display: "block", marginTop: 2, fontSize: 12, opacity: 0.92 }}>
                {paymentHistory.length} payment
                {paymentHistory.length === 1 ? "" : "s"}
                {totalPaid > 0 ? ` · Total ${formatAmount(totalPaid)}` : ""}
              </small>
            </div>
                 </div>
   
          <button
            type="button"
            onClick={() => setIsOpenSessionDialog(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              border: "1px solid rgba(255,255,255,0.45)",
              borderRadius: 999,
              padding: "7px 12px",
              background: "rgba(255,255,255,0.14)",
              color: COLORS.white,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <i className="fa fa-calendar" aria-hidden />
            {displaySessionName}
            <i className="fa fa-exchange" aria-hidden style={{ opacity: 0.85 }} />
          </button>
               </div>

        <div style={{ padding: "16px 40px 24px" }}>
          {loading && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <MyLoader />
                          </div>
          )}

          {!loading && student && (
            <StudentDetailsCard
              student={student}
              sessionName={resolvedSessionName}
            />
          )}

          {!loading && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa fa-list-alt" style={{ color: ACCENT, fontSize: 14 }} aria-hidden />
                  <h5
                    style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                      color: "#374151",
                    }}
                  >
                    Transactions
                  </h5>
                      </div>
                  </div>
                 
              {paymentHistory.length === 0 ? (
                <div
                  style={{
                    padding: "28px 16px",
                    borderRadius: 12,
                    background: "#f8fafc",
                    border: "1px dashed #cbd5e1",
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: 13,
                  }}
                >
                  No payment records found for this session.
                 </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 12,
                  }}
                >
                  {paymentHistory.map((item, index) => (
                    <PaymentCard
                      key={item.transaction_id || index}
                      item={item}
                    />
                  ))}
               </div>
              )}
            </>
          )}
    </div>
</div>

      {isOpenSessionDialog && (
        <MdlSelectSession
          action={handleCloseSessionDialog}
          action2={handleSelectedSession}
        />
      )}
  </div>  
          )}
        />
        <Route
          path="/AdminMain/PaymentDetails/:transaction_id/:session_id/:student_id/:transaction_for"
          component={PaymentDetails}
        />
        <Route
          path="/AdminMain/FeeReceipt/:transaction_id/:student_id/:session_id"
          component={FeeReceipt}
        />
      </Switch>
   </Router>
  );
};

export default PaymentHistory;
